/*
  Sväto · Vu sur Instagram (bloc svato-video-card). Voir assets/CUSTOM.txt.
  - Une seule vidéo joue à la fois : la carte la plus visible (IntersectionObserver), toujours muette.
  - prefers-reduced-motion : aucune lecture automatique, lecture au clic seulement.
  - Piste (groupe .svato-ig-track) : glisser à la souris + flèches rondes (ordinateur).
*/
const reduced = matchMedia('(prefers-reduced-motion: reduce)');

// Toutes les vidéos visibles jouent en boucle, en même temps ; hors écran elles se mettent en pause
// (économie de batterie et de bande passante) et reprennent en revenant à l'écran.
const observer = new IntersectionObserver(
  (entries) => entries.forEach((e) => (e.isIntersecting ? e.target.play() : e.target.pause())),
  { rootMargin: '100px 0px' }
);

class SvatoVideoCard extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    if (reduced.matches) {
      this.video.removeAttribute('autoplay');
      this.video.pause();
    }
    this.video.addEventListener('playing', () => this.classList.add('is-playing'));
    this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
    // prefers-reduced-motion : pas de lecture automatique, lecture/pause au clic.
    this.querySelector('[data-vc-play]')?.addEventListener('click', () => this.play(true));
    this.video.addEventListener('click', () => (this.video.paused ? this.play(true) : this.pause()));
    observer.observe(this);
  }

  disconnectedCallback() {
    observer.unobserve(this);
  }

  play(force = false) {
    if (!this.video || !this.video.paused) return;
    if (reduced.matches && !force) return;
    if (this.video.preload === 'none') this.video.preload = 'auto';
    this.video.muted = true;
    this.video
      .play()
      .then(() => this.classList.add('is-playing'))
      .catch(() => this.classList.remove('is-playing'));
  }

  pause() {
    if (!this.video) return;
    if (!this.video.paused) this.video.pause();
    this.classList.remove('is-playing');
  }
}

if (!customElements.get('svato-video-card')) customElements.define('svato-video-card', SvatoVideoCard);

/* ---------- Piste ---------- */
const ARROW = (dir) =>
  `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="${dir === 'next' ? 'm7.5 5 5 5-5 5' : 'm12.5 15-5-5 5-5'}" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function initTrack(track) {
  if (track.dataset.igReady) return;
  track.dataset.igReady = '1';
  // Flèches en haut à droite, dans l'en-tête de la section (groupe .svato-ig-head).
  const section = track.closest('.shopify-section') || track.parentElement;
  const head = section.querySelector('.svato-ig-head');
  const nav = document.createElement('div');
  nav.className = 'svato-ig-nav';
  nav.setAttribute('role', 'group');
  nav.setAttribute('aria-label', 'Faire défiler les vidéos');
  const arrows = ['prev', 'next'].map((dir) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `button button--secondary svato-ig-arrow svato-ig-arrow--${dir}`;
    btn.setAttribute('aria-label', dir === 'next' ? 'Vidéos suivantes' : 'Vidéos précédentes');
    btn.innerHTML = ARROW(dir);
    btn.addEventListener('click', () => {
      const card = track.querySelector('svato-video-card');
      const step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : 300;
      track.scrollBy({ left: dir === 'next' ? step : -step, behavior: reduced.matches ? 'auto' : 'smooth' });
    });
    nav.append(btn);
    return btn;
  });
  // Colonne de droite de l'en-tête : pastille @svato.fr (ordinateur) + flèches.
  const aside = document.createElement('div');
  aside.className = 'svato-ig-aside';
  aside.append(nav);
  (head || track.parentElement).append(aside);
  // La pastille @svato.fr est dans la phrase du sous-titre : sur ordinateur on la place à droite,
  // à côté des flèches ; en mobile elle reste dans la phrase, après « Instagram : ».
  const handle = section.querySelector('.svato-ig-sub a');
  if (handle) {
    handle.classList.add('svato-ig-handle');
    const spot = document.createComment('svato-ig-handle');
    handle.after(spot);
    const desktop = matchMedia('(min-width: 750px)');
    const place = () => (desktop.matches ? aside.prepend(handle) : spot.before(handle));
    desktop.addEventListener('change', place);
    place();
  }
  const [prev, next] = arrows;

  const update = () => {
    const max = track.scrollWidth - track.clientWidth;
    nav.hidden = max <= 4;
    prev.disabled = track.scrollLeft <= 4;
    next.disabled = track.scrollLeft >= max - 4;
  };
  track.addEventListener('scroll', update, { passive: true });
  new ResizeObserver(update).observe(track);
  update();

  // Glisser à la souris (le tactile garde le défilement natif).
  let startX = 0;
  let startScroll = 0;
  let moved = false;
  track.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button')) return;
    startX = e.clientX;
    startScroll = track.scrollLeft;
    moved = false;
    const move = (ev) => {
      const dx = ev.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        track.classList.add('is-dragging');
      }
      if (moved) track.scrollLeft = startScroll - dx;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      requestAnimationFrame(() => track.classList.remove('is-dragging'));
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  });
  // Un glisser ne doit pas ouvrir le produit.
  track.addEventListener(
    'click',
    (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    },
    true
  );
  track.addEventListener('dragstart', (e) => e.preventDefault());
}

const initTracks = () => document.querySelectorAll('.svato-ig-track').forEach(initTrack);
initTracks();
document.addEventListener('shopify:section:load', initTracks);
